/**
 * Text overlays burned into videos before they go to Instagram / Facebook.
 *
 * One drawing routine renders both the live preview (small canvas over the
 * cropper) and the exported file (full-size canvas), so what the user frames
 * is exactly what gets encoded. Positions and sizes are fractions of the
 * output frame so they survive the preview → 1080×1920 scale-up.
 */

export type VideoCaptionStyleId =
  | 'classic'
  | 'pill'
  | 'highlight'
  | 'outline'
  | 'neon'
  | 'script'
  | 'serif'
  | 'marker'
  | 'typewriter';

export type VideoCaption = {
  text: string;
  style: VideoCaptionStyleId;
  /** Hex colour. Meaning depends on the style (text, or box for "highlight"). */
  color: string;
  /** Centre of the text block as fractions of the frame (0–1). */
  x: number;
  y: number;
  /** Font size as a fraction of the frame height. */
  size: number;
};

export type VideoCaptionBounds = { x: number; y: number; width: number; height: number };

export type VideoCaptionStyle = {
  id: VideoCaptionStyleId;
  label: string;
  /** CSS font-family stack (first family is the Google font when any). */
  fontFamily: string;
  googleFamily?: string;
  weight: number;
  italic?: boolean;
  uppercase?: boolean;
  /** Fraction of font size. */
  letterSpacing?: number;
  lineHeight: number;
  background: 'none' | 'dark' | 'light' | 'color';
  /** Draw a box per line (Instagram "highlight") instead of one block. */
  perLineBox?: boolean;
  stroke?: boolean;
  shadow: 'none' | 'soft' | 'hard' | 'glow';
  /** Text colour is fixed by the style (colour swatch recolours nothing). */
  fixedTextColor?: string;
};

export const VIDEO_CAPTION_STYLES: VideoCaptionStyle[] = [
  {
    id: 'classic',
    label: 'Classic',
    fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
    googleFamily: 'Montserrat',
    weight: 800,
    lineHeight: 1.18,
    background: 'none',
    shadow: 'soft',
  },
  {
    id: 'pill',
    label: 'Pill',
    fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
    googleFamily: 'Montserrat',
    weight: 700,
    lineHeight: 1.25,
    background: 'dark',
    shadow: 'none',
  },
  {
    id: 'highlight',
    label: 'Highlight',
    fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
    googleFamily: 'Montserrat',
    weight: 800,
    lineHeight: 1.3,
    background: 'color',
    perLineBox: true,
    shadow: 'none',
  },
  {
    id: 'outline',
    label: 'Outline',
    fontFamily: '"Anton", Impact, "Arial Black", sans-serif',
    googleFamily: 'Anton',
    weight: 400,
    uppercase: true,
    letterSpacing: 0.03,
    lineHeight: 1.08,
    background: 'none',
    stroke: true,
    shadow: 'hard',
  },
  {
    id: 'neon',
    label: 'Neon',
    fontFamily: '"Bebas Neue", "Oswald", Impact, sans-serif',
    googleFamily: 'Bebas Neue',
    weight: 400,
    uppercase: true,
    letterSpacing: 0.06,
    lineHeight: 1.05,
    background: 'none',
    shadow: 'glow',
  },
  {
    id: 'script',
    label: 'Script',
    fontFamily: '"Pacifico", "Brush Script MT", cursive',
    googleFamily: 'Pacifico',
    weight: 400,
    lineHeight: 1.45,
    background: 'none',
    shadow: 'soft',
  },
  {
    id: 'serif',
    label: 'Serif',
    fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
    googleFamily: 'Playfair Display',
    weight: 700,
    italic: true,
    lineHeight: 1.2,
    background: 'none',
    shadow: 'soft',
  },
  {
    id: 'marker',
    label: 'Marker',
    fontFamily: '"Permanent Marker", "Comic Sans MS", cursive',
    googleFamily: 'Permanent Marker',
    weight: 400,
    lineHeight: 1.25,
    background: 'none',
    shadow: 'hard',
  },
  {
    id: 'typewriter',
    label: 'Typewriter',
    fontFamily: '"Courier Prime", "Courier New", Courier, monospace',
    googleFamily: 'Courier Prime',
    weight: 700,
    lineHeight: 1.3,
    background: 'light',
    perLineBox: true,
    shadow: 'none',
    fixedTextColor: '#111111',
  },
];

export const VIDEO_CAPTION_COLORS = [
  '#FFFFFF',
  '#111111',
  '#FFD60A',
  '#FF375F',
  '#FF9F0A',
  '#30D158',
  '#0A84FF',
  '#BF5AF2',
];

export const VIDEO_CAPTION_MAX_CHARS = 120;
export const VIDEO_CAPTION_MIN_SIZE = 0.02;
export const VIDEO_CAPTION_MAX_SIZE = 0.16;

export function captionStyle(id: VideoCaptionStyleId | string | undefined): VideoCaptionStyle {
  return VIDEO_CAPTION_STYLES.find((s) => s.id === id) || VIDEO_CAPTION_STYLES[0];
}

export function defaultVideoCaption(text = ''): VideoCaption {
  return { text, style: 'classic', color: '#FFFFFF', x: 0.5, y: 0.8, size: 0.045 };
}

export function clampCaptionSize(size: number): number {
  if (!Number.isFinite(size)) return 0.045;
  return Math.min(VIDEO_CAPTION_MAX_SIZE, Math.max(VIDEO_CAPTION_MIN_SIZE, size));
}

export function hasCaptionText(caption: VideoCaption | null | undefined): caption is VideoCaption {
  return !!caption && caption.text.trim().length > 0;
}

/** Black or white, whichever reads better on `hex`. */
export function contrastTextColor(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#111111';
  const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return lum > 0.6 ? '#111111' : '#FFFFFF';
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex) || { r: 255, g: 255, b: 255 };
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

// ── Fonts ────────────────────────────────────────────────────────────────

const FONTS_LINK_ID = 'nexus-video-caption-fonts';
let fontsPromise: Promise<void> | null = null;

function googleFontsHref(): string {
  const families = [
    'Montserrat:wght@700;800',
    'Anton',
    'Bebas+Neue',
    'Pacifico',
    'Playfair+Display:ital,wght@1,700',
    'Permanent+Marker',
    'Courier+Prime:wght@700',
  ];
  return `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join('&')}&display=swap`;
}

/**
 * Load the caption fonts once (Google Fonts) and wait until they are usable
 * on a canvas. Resolves even if a font fails — fallbacks still render.
 */
export function ensureCaptionFontsLoaded(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  if (fontsPromise) return fontsPromise;
  if (!document.getElementById(FONTS_LINK_ID)) {
    const link = document.createElement('link');
    link.id = FONTS_LINK_ID;
    link.rel = 'stylesheet';
    link.href = googleFontsHref();
    document.head.appendChild(link);
  }
  const loads = VIDEO_CAPTION_STYLES.filter((s) => s.googleFamily).map((s) =>
    document.fonts
      .load(`${s.italic ? 'italic ' : ''}${s.weight} 32px "${s.googleFamily}"`)
      .then(() => undefined)
      .catch(() => undefined),
  );
  fontsPromise = Promise.race([
    Promise.all(loads).then(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, 4000)),
  ]);
  return fontsPromise;
}

// ── Drawing ──────────────────────────────────────────────────────────────

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const para of text.split('\n')) {
    let line = '';
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const test = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(test).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    else if (para.trim() === '' && lines.length > 0) lines.push('');
  }
  while (lines.length && lines[lines.length - 1] === '') lines.pop();
  return lines.slice(0, 5);
}

function applyLetterSpacing(ctx: CanvasRenderingContext2D, px: number) {
  // Supported in Chrome/Safari 17+; harmless elsewhere.
  if ('letterSpacing' in ctx) {
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${px}px`;
  }
}

/**
 * Draw a caption into a frame of `width`×`height` CSS/output pixels.
 * Returns the bounding box of what was drawn (for hit testing), or null
 * when there is nothing to draw.
 */
export function drawVideoCaption(
  ctx: CanvasRenderingContext2D,
  caption: VideoCaption,
  width: number,
  height: number,
): VideoCaptionBounds | null {
  const raw = caption.text.trim();
  if (!raw) return null;
  const style = captionStyle(caption.style);
  const text = style.uppercase ? raw.toUpperCase() : raw;
  const fontSize = Math.max(6, clampCaptionSize(caption.size) * height);
  const spacing = (style.letterSpacing || 0) * fontSize;

  ctx.save();
  ctx.font = `${style.italic ? 'italic ' : ''}${style.weight} ${fontSize}px ${style.fontFamily}`;
  applyLetterSpacing(ctx, spacing);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const maxTextWidth = width * 0.86;
  const lines = wrapLines(ctx, text, maxTextWidth);
  if (lines.length === 0) {
    ctx.restore();
    return null;
  }

  const lineHeight = fontSize * style.lineHeight;
  const hasBox = style.background !== 'none';
  const padX = hasBox ? fontSize * 0.55 : fontSize * 0.1;
  const padY = hasBox ? fontSize * (style.perLineBox ? 0.18 : 0.4) : fontSize * 0.1;
  const lineWidths = lines.map((l) => ctx.measureText(l).width + (l.length > 0 ? spacing : 0));
  const maxLine = Math.max(...lineWidths);
  const boxW = Math.min(width * 0.96, maxLine + padX * 2);
  const boxH = lines.length * lineHeight + padY * 2;

  // Keep the block on-screen even if the stored centre drifts.
  const halfW = boxW / 2;
  const halfH = boxH / 2;
  const cx = Math.min(Math.max(caption.x * width, halfW), Math.max(halfW, width - halfW));
  const cy = Math.min(Math.max(caption.y * height, halfH), Math.max(halfH, height - halfH));
  const boxX = cx - halfW;
  const boxY = cy - halfH;

  const textColor = style.fixedTextColor
    ? style.fixedTextColor
    : style.background === 'color'
      ? contrastTextColor(caption.color)
      : caption.color;

  // Background boxes
  if (style.background !== 'none') {
    const fill =
      style.background === 'dark'
        ? 'rgba(0,0,0,0.55)'
        : style.background === 'light'
          ? 'rgba(255,255,255,0.9)'
          : caption.color;
    ctx.fillStyle = fill;
    if (style.perLineBox) {
      const radius = fontSize * 0.28;
      lines.forEach((line, i) => {
        if (!line) return;
        const w = Math.min(boxW, lineWidths[i] + padX * 2);
        const x = cx - w / 2;
        const y = boxY + padY + lineHeight * i - (lineHeight - fontSize) / 2 - fontSize * 0.08;
        const h = lineHeight + fontSize * 0.16;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, radius);
        ctx.fill();
      });
    } else {
      const radius = Math.min(fontSize * 0.5, boxH / 2);
      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxW, boxH, radius);
      ctx.fill();
    }
  }

  // Shadow / glow / stroke applied per text pass
  const drawLines = (fillStyle: string) => {
    ctx.fillStyle = fillStyle;
    lines.forEach((line, i) => {
      if (!line) return;
      const y = boxY + padY + lineHeight * i + lineHeight / 2;
      ctx.fillText(line, cx + spacing / 2, y, boxW - padX * 2);
    });
  };
  const strokeLines = (strokeStyle: string, lineWidth: number) => {
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;
    ctx.lineJoin = 'round';
    lines.forEach((line, i) => {
      if (!line) return;
      const y = boxY + padY + lineHeight * i + lineHeight / 2;
      ctx.strokeText(line, cx + spacing / 2, y, boxW - padX * 2);
    });
  };

  if (style.shadow === 'glow') {
    ctx.shadowColor = rgba(caption.color, 0.95);
    ctx.shadowBlur = fontSize * 0.55;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    drawLines(textColor);
    ctx.shadowBlur = fontSize * 0.2;
    drawLines('#FFFFFF');
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    drawLines(rgba(caption.color, 0.55));
  } else {
    if (style.shadow === 'soft') {
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = fontSize * 0.28;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = fontSize * 0.06;
    } else if (style.shadow === 'hard') {
      ctx.shadowColor = 'rgba(0,0,0,0.85)';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = fontSize * 0.05;
      ctx.shadowOffsetY = fontSize * 0.06;
    }
    if (style.stroke) {
      strokeLines(contrastTextColor(textColor) === '#FFFFFF' ? '#FFFFFF' : '#000000', fontSize * 0.14);
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }
    drawLines(textColor);
  }

  ctx.restore();
  return { x: boxX, y: boxY, width: boxW, height: boxH };
}
